import { useMemo } from 'react'
import type { Tag, TagGroupDef } from '../types'

export interface TagRow {
  group: Tag['group']
  label: string
  items: Tag[]
}

/**
 * The filter's rows: one per category, in the intended order.
 *
 * Shared by the website and the Miro panel because the RULES are the same —
 * which categories exist, what order they read in, which are hidden, and when a
 * tag is worth showing. Only the shapes differ: the website has room for a
 * label beside its chips, the panel puts it above them.
 *
 * The categories arrive as data from the `tag_groups` table rather than from a
 * constant in the source. Naming groups in code is how the panel's tag strip
 * once emptied itself: the two it asked for stopped existing the day the tags
 * were re-filed. A group that somehow is not in the table still gets a row,
 * after the known ones, rather than taking its tags down with it.
 */
export function useTagRows(
  tags: Tag[],
  activeTagIds: string[],
  groups: TagGroupDef[],
): TagRow[] {
  return useMemo(() => {
    const known = groups.filter((group) => !group.hidden)
    const hidden = new Set(groups.filter((group) => group.hidden).map((group) => group.slug))
    const listed = new Set(groups.map((group) => group.slug))

    const orphans = [...new Set(tags.map((tag) => tag.group))]
      .filter((slug) => !listed.has(slug))
      .sort()
      .map((slug) => ({ slug, label: slug, position: 9999, hidden: false }))

    return [...known, ...orphans]
      .filter((group) => !hidden.has(group.slug))
      .map((group) => ({
        group: group.slug,
        label: group.label,
        // A tag matching nothing is noise, but one that is switched on stays put
        // so it can be switched off again.
        items: tags.filter(
          (tag) =>
            tag.group === group.slug &&
            ((tag.assetCount ?? 1) > 0 || activeTagIds.includes(tag.id)),
        ),
      }))
  }, [tags, activeTagIds, groups])
}
